<template>
    <div class="my-4 w-full">
        <div class="mb-1 flex items-center justify-between">
            <label class="block text-sm font-medium">
                {{ $t('repayment_schedule') }}
            </label>
            <button
                type="button"
                class="btn btn-secondary btn-sm"
                @click="addRow"
            >
                <fa icon="fa-plus" />
                {{ $t('add') }}
            </button>
        </div>
        <p class="mb-2 text-xs text-gray-500">
            {{ $t('help_repayment_schedule') }}
        </p>

        <div
            v-if="rows.length > 0"
            class="space-y-2"
        >
            <div
                v-for="(row, index) in rows"
                :key="index"
                class="flex items-center gap-2"
            >
                <input
                    v-model="row.date"
                    type="date"
                    class="form-input w-1/2"
                    @input="emitUpdate"
                />
                <input
                    v-model.number="row.amount_pct"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    class="form-input w-1/2"
                    :placeholder="$t('amount_pct')"
                    @input="emitUpdate"
                />
                <button
                    type="button"
                    class="bg-danger-600 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-white"
                    :title="$t('remove')"
                    @click="removeRow(index)"
                >
                    <fa icon="fas fa-trash-alt" />
                </button>
            </div>
        </div>
        <div
            v-else
            class="border-divider bg-background-muted rounded-md border border-dashed p-4 text-center"
        >
            <span class="text-muted text-sm">
                {{ $t('help_repayment_schedule_empty') }}
            </span>
        </div>

        <p
            class="mt-2 text-xs"
            :class="totalPct === 100 ? 'text-gray-500' : 'text-danger-500'"
        >
            {{ $t('repayment_schedule_total', { total: totalPct }) }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { RepaymentScheduleItem } from '@src/types'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
    modelValue?: RepaymentScheduleItem[] | null
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: RepaymentScheduleItem[]): void
}>()

const rows = ref<RepaymentScheduleItem[]>([])

watch(
    () => props.modelValue,
    (newVal) => {
        rows.value = Array.isArray(newVal) ? [...newVal] : []
    },
    { immediate: true, deep: true }
)

const totalPct = computed(() =>
    rows.value.reduce((sum, row) => sum + (row.amount_pct ?? 0), 0)
)

function emitUpdate(): void {
    emit('update:modelValue', rows.value)
}

function addRow(): void {
    rows.value.push({ date: null, amount_pct: null })
    emitUpdate()
}

function removeRow(index: number): void {
    rows.value.splice(index, 1)
    emitUpdate()
}
</script>
